import { FC, useEffect, useMemo, useState } from 'react';
import {
  SweepFilters,
  SweepKebabOption,
  SweepProps,
  UserRole,
} from './sweeps.type';
import { SweepsTableHeader } from './sweepsTableHeader';
import Table from '../../component/table/table';
import { getLoadingActionMenu } from '../userManagement/userManagementHelper';
import {
  getMidnightTimestampsForDate,
  getSweepKebabOptions,
  getSweepTableHeader,
  handleSweepFilters,
  InitalFilters,
} from './sweepHelper';
import SweepsTableFilter from './sweepsTableFilter';
import {
  useApproveSummary,
  useDeleteSweep,
  useDownloadSweepReport,
  useSweepsSummary,
} from '../../hooks/useSweepData';
import { useOrganizationContext } from '../../service/context/organizationContext';
import SweepViewModal from '../../component/sweepViewModal/sweepViewModal';
import SweepReport from './sweepViewReport';
import moment from 'moment';
import { Player } from '@inplay/models/dist/src/player';
import {
  POIClassification,
  SweepSignature,
  UpdateSweepResponse,
  UpdateUserResponse,
} from '@inplay/models';
import SweepEditModal from '../../component/sweepEditModal/sweepEditModal';
import { useUsersSummary } from '../../hooks/useUserData';
import SignatureModal from './addSignatureModal';
import ConfirmationModal from '../../component/confirmationModal/confirmationModal';
import { Trans, useTranslation } from 'react-i18next';
import {
  apiStatusCode,
  DownloadStatusResponse,
  StatusResponse,
  SuccessMessage,
  Sweep,
  SweepStatusResponse,
} from '../../utils/helper.type';
import CenterModal from '../../component/centerModal/centerModal';
import DatePicker from '../../component/datePicker/datePicker';
import { DateRange } from 'react-day-picker';
import { Mode } from '../../component/datePicker/datePicker.type';
import { isUndefined } from 'lodash';
import debounce from 'lodash.debounce';
import SuccessToast from '../../component/successModal/successModal';
import { getStartAndEndUnixTimestamps } from '../../utils/helper';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import ModalComponent from '../../component/modal/modal';
import EditHistoryList from './editHistoryList';

const Sweeps: FC<SweepProps> = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const [selectedSweeps, setSelectedSweeps] = useState<Sweep[]>([]);
  const [selectedSweepRowForModal, setSelectedSweepRowForModal] =
    useState<Sweep>();
  const [showSweepViewModal, setShowSweepViewModal] = useState<boolean>(false);
  const [sweepSearchQuery, setUserSearchQuery] = useState<string>('');
  const [showSweepEditModal, setShowSweepEditModal] = useState<boolean>(false);
  const [showApproveSignatureModal, setShowApproveSignatureModal] =
    useState<boolean>(false);
  const [selectedApproveRange, setSelectedApproveRange] = useState<
    DateRange | undefined
  >(undefined);
  const [showAlreadyApproveModal, setShowAlreadyApproveModal] =
    useState<boolean>(false);
  const [showEditHistoryModal, setShowEditHistoryModal] =
    useState<boolean>(false);
  const [showConfirmApproveModal, setShowConfirmApprove] =
    useState<boolean>(false);
  const [showApproveDateModal, setShowApproveDateModal] = useState(false);
  const [approveSignature, setApproveSignature] = useState<SweepSignature>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] =
    useState<boolean>(false);
  const [showDeleteErrorToast, setShowDeleteErrorToast] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const { selectedVenueBranch } = useOrganizationContext();
  const selectedBranchIDs: string[] = useMemo(() => {
    return selectedVenueBranch ? [selectedVenueBranch._id] : [];
  }, [selectedVenueBranch]);
  const [showApproveSuccessToast, setShowApproveSuccessToast] =
    useState<boolean>(false);
  const [filters, setFilters] = useState<SweepFilters>(InitalFilters);
  const [filteredData, setFilteredData] = useState<Sweep[]>([]);

  const { fetchSweepSummary, sweepsSummary, sweepsSummaryLoading } =
    useSweepsSummary({
      startTime: undefined,
      endTime: undefined,
      venueBranchIds: selectedBranchIDs,
    });

  const { downloadSweepReport, downloadSweepReportLoading } =
    useDownloadSweepReport();

  const { deleteSweep, deleteSweepLoading } = useDeleteSweep();

  const { usersSummary } = useUsersSummary(true);
  const { approveSummary, approveLoading } = useApproveSummary();
  const { isTablet } = useResponsiveLayout();

  const { startTime, endTime } = useMemo(() => {
    if (!selectedDate) return { startTime: null, endTime: null };
    setFilters({
      ...filters,
      dateRange: { from: selectedDate, to: undefined },
    });
    return getMidnightTimestampsForDate(selectedDate);
  }, [selectedDate]);

  const debouncedFetchSweepSummary = useMemo(
    () =>
      debounce((startTime, endTime, branchIDs) => {
        fetchSweepSummary({
          startTime: startTime.toString(),
          endTime: endTime.toString(),
          venueBranchIds: branchIDs,
        });
      }, 700),
    [],
  );

  useEffect(() => {
    if (!selectedBranchIDs.length || startTime === null || endTime === null)
      return;

    debouncedFetchSweepSummary(startTime, endTime, selectedBranchIDs);
  }, [selectedBranchIDs, startTime, endTime]);

  const handleClearFilter = () => {
    setFilters(InitalFilters);
    handleFilterApply(InitalFilters, true);
  };

  const handleFilterApply = (
    filtersFromModal?: SweepFilters,
    fromClear?: boolean,
  ) => {
    if (filtersFromModal) {
      setSelectedDate(filtersFromModal.dateRange.from ?? selectedDate);
      setFilters(filtersFromModal);
    }
    const filtered: Sweep[] = handleSweepFilters(
      sweepsSummary,
      sweepSearchQuery,
      fromClear ? InitalFilters : filtersFromModal ?? filters,
    );
    setFilteredData(filtered);
    if (!fromClear) {
      setOpenFilterModal(false);
    }
  };

  useEffect(() => {
    if (selectedSweepRowForModal) {
      const updatedSweep: Sweep | undefined = sweepsSummary.find(
        (sweep: Sweep) => sweep._id === selectedSweepRowForModal._id,
      );
      if (updatedSweep) setSelectedSweepRowForModal(updatedSweep);
    }
  }, [sweepsSummary]);

  useEffect(() => {
    handleFilterApply(undefined, true);
  }, [sweepsSummary, sweepSearchQuery]);

  const handleDownloadSweepReports = async (
    sweepIds: string[],
  ): Promise<DownloadStatusResponse | void> => {
    if (downloadSweepReportLoading) {
      console.log(
        `[DownloadSweepReport] Already in progress... skipping this request`,
      );
      return;
    }
    console.log(`[SELECTED SWEEP IDS...]`, sweepIds);
    try {
      console.log(`[ENTRY][DOWNLOAD SWEEP]`);
      const sweepDownloadResponse: string[] =
        await downloadSweepReport(sweepIds);

      const response: DownloadStatusResponse = {
        statusCode: apiStatusCode.SUCCESS,
        data: sweepDownloadResponse,
      };
      console.log(`[DownloadSweepReport][Response]...`);

      if (response.statusCode === apiStatusCode.SUCCESS) {
        console.log(`[DownloadSweepReport][Success]`, response);
        setSelectedSweeps([]);
      }
      return response;
    } catch (error: unknown) {
      console.warn(`[DownloadSweepReport][Error]`, { error });
      throw error;
    } finally {
      console.log(`[DownloadSweepReport] finally block...`);
    }
  };

  const handleKebabActionFromKebabMenu = async (
    action: string,
    row: Sweep,
    rowIndex: number,
  ): Promise<void> => {
    setActiveRowIndex(rowIndex);
    if (action === SweepKebabOption.Edit) {
      setSelectedSweepRowForModal(row);
      setShowSweepEditModal(true);
    }
    if (action === SweepKebabOption.ViewEditHistory) {
      setSelectedSweepRowForModal(row);
      setShowEditHistoryModal(true);
    }
    if (action === SweepKebabOption.Download) {
      setSelectedSweepRowForModal(row);
      handleDownloadSweepReports([row?._id]);
    }
  };

  const loadingActionMenu: { rowIndex: number; action: string } =
    getLoadingActionMenu(
      activeRowIndex,
      false,
      false,
      '',
      downloadSweepReportLoading,
    ) ?? { rowIndex: -1, action: '' };

  const handleSelectionChange = (selectedSweeps: Sweep[]) => {
    setSelectedSweeps(selectedSweeps);
  };

  const handleUserSearchChange = (value: string) => {
    setUserSearchQuery(value);
  };

  const handleViewSweepReportModal = (row: Sweep) => {
    setShowSweepViewModal(true);
    setSelectedSweepRowForModal(row);
  };

  const poiCount: number =
    selectedSweepRowForModal?.players?.filter(
      (player: Player) => player.poiClassification === POIClassification.POI,
    ).length ?? 0;

  const handleApprove = () => {
    if (selectedSweeps.length === 0) {
      setShowApproveDateModal(true);
    } else {
      setShowApproveSignatureModal(true);
    }
  };

  const handleApply = (range: DateRange) => {
    setSelectedApproveRange(range);
  };

  const handleAddSignature = (signature: SweepSignature) => {
    setApproveSignature(signature);
    setShowConfirmApprove(true);
    setShowApproveSignatureModal(false);
  };

  const handleApproveConfirm = async () => {
    if (approveLoading) {
      console.log(
        '[ApproveReport] Already in progress... skipping this request',
      );
      return;
    }
    const sweepIds: string[] = selectedSweeps.map((sweep: Sweep) => sweep._id);

    try {
      const { startTimestamp, endTimestamp } =
        getStartAndEndUnixTimestamps(selectedApproveRange);

      const payload: {
        sweepIds?: string[];
        venueBranchId: string;
        startTime?: number;
        endTime?: number;
        reviewedBy: {
          firstName: string;
          lastName: string;
          _id: string;
          notes: string;
          signature: string;
          signedDate: Date;
        };
      } = {
        venueBranchId: selectedVenueBranch?._id ?? '',
        reviewedBy: {
          firstName: approveSignature?.firstName ?? '',
          lastName: approveSignature?.lastName ?? '',
          _id: approveSignature?._id ?? '',
          notes: approveSignature?.notes ?? '',
          signature: approveSignature?.signatureId ?? '',
          signedDate: approveSignature?.signedDate ?? new Date(),
        },
      };

      if (sweepIds.length === 0) {
        payload.startTime = startTimestamp;
        payload.endTime = endTimestamp;
      }

      if (sweepIds.length > 0) {
        payload.sweepIds = sweepIds;
      }
      const approveKeyResponse: UpdateUserResponse | undefined =
        await approveSummary(payload);

      if (!isUndefined(approveKeyResponse)) {
        const approveResponse: StatusResponse | undefined = {
          statusCode: apiStatusCode.SUCCESS,
          data: approveKeyResponse,
        };

        console.log(`[Approve Report] response`, approveResponse);

        if (approveResponse?.statusCode === apiStatusCode.SUCCESS) {
          console.log(`[ApproveReport] call fetch approve api `);

          try {
            console.log(`[SweepReport] call fetch sweep api `);
            const sweepSummaryKeyResponse: Sweep[] | undefined =
              await fetchSweepSummary({
                startTime: startTime?.toString() ?? '',
                endTime: endTime?.toString() ?? '',
                venueBranchIds: selectedBranchIDs,
              });

            if (!isUndefined(sweepSummaryKeyResponse)) {
              const summaryResponse: SweepStatusResponse =
                sweepSummaryKeyResponse && {
                  statusCode: apiStatusCode.SUCCESS,
                  data: sweepSummaryKeyResponse,
                };
              console.log(`[SummaryResponse] response `, summaryResponse);
              if (summaryResponse?.statusCode === apiStatusCode.SUCCESS) {
                console.log(
                  `[SummaryResponse] close modal `,
                  approveResponse?.data?.matchedCount,
                );
                setShowConfirmApprove(false);
                setShowApproveSuccessToast(true);
                if (
                  approveResponse?.data?.matchedCount !==
                  approveResponse?.data?.modifiedCount
                ) {
                  setShowApproveSuccessToast(true);
                  setShowAlreadyApproveModal(true);
                }
              }
            }
          } catch (error: unknown) {
            console.error('Error fetching sweep summary', error);
          } finally {
            console.error('[SummaryResponse] finally block');
          }

          setSelectedSweeps([]);
          setSelectedApproveRange(undefined);
        }
      }
    } catch (error: unknown) {
      console.warn(`[ApproveReport]`, { error });
      throw error;
    } finally {
      console.warn(`[ApproveReport] finally block...`);
    }
  };

  const handleNext = () => {
    setShowApproveSignatureModal(true);
    setShowApproveDateModal(false);
  };

  const handleClearSweeps = () => {
    setSelectedSweeps([]);
  };

  const handleDeleteSweeps: () => void = () => {
    setShowDeleteConfirmation(true);
  };

  const handleDownloadSweeps: () => void = () => {
    const sweepIds: string[] = selectedSweeps.map((sweep: Sweep) => sweep._id);
    handleDownloadSweepReports(sweepIds);
  };

  const handleConfirmSweepDelete: () => void = async () => {
    if (deleteSweepLoading) {
      console.log(
        '[DeleteSweepReport] Already in progress... skipping this request',
      );
      return;
    }
    const selectedSweepIds: string[] = selectedSweeps.map(
      (sweep: Sweep) => sweep?._id,
    );
    console.log(`[SELECTED SWEEPS IDS...]`, selectedSweepIds);
    try {
      const deleteSweepRowResponse: UpdateSweepResponse | undefined =
        await deleteSweep(selectedSweepIds);
      const deleteSweepResponse: StatusResponse | undefined =
        deleteSweepRowResponse
          ? {
              statusCode: apiStatusCode.SUCCESS,
              data: deleteSweepRowResponse,
            }
          : undefined;
      console.log(`[DeleteSweepReport] response`, deleteSweepResponse);

      if (deleteSweepResponse?.statusCode === apiStatusCode.SUCCESS) {
        console.log(`[DeleteSweepReport] call fetch sweep api `);
        try {
          const sweepSummaryKeyResponse: Sweep[] | undefined =
            await fetchSweepSummary({
              startTime: startTime?.toString() ?? '',
              endTime: endTime?.toString() ?? '',
              venueBranchIds: selectedBranchIDs,
            });

          if (!isUndefined(sweepSummaryKeyResponse)) {
            const summaryResponse: SweepStatusResponse =
              sweepSummaryKeyResponse && {
                statusCode: apiStatusCode.SUCCESS,
                data: sweepSummaryKeyResponse,
              };
            console.log(`[SummaryResponse] response `, summaryResponse);
            if (summaryResponse?.statusCode === apiStatusCode.SUCCESS) {
              setShowDeleteConfirmation(false);
              setShowDeleteSuccessToast(true);
            }
          }
        } catch (error: unknown) {
          console.error('Error fetching sweep summary', error);
        } finally {
          console.error('[SummaryResponse] finally block');
        }
      }
    } catch (error: unknown) {
      console.warn(`[DeleteSweep]`, { error });
      setShowDeleteConfirmation(false);
      setShowDeleteErrorToast(true);
      setSelectedSweeps([]);
    } finally {
      console.warn(`[DeleteSweep] finally block...`);
    }
  };

  return (
    <div className="header-content">
      {showApproveSuccessToast && (
        <SuccessToast
          message={
            selectedSweeps.length > 0
              ? SuccessMessage.APPROVE_MULTIPLE_REPORT_SUCCESSFULLY
              : SuccessMessage.APPROVE_SINGLE_REPORT_SUCCESSFULLY
          }
          onClose={() => setShowApproveSuccessToast(false)}
          className="bottom-15 bg-white right-10"
        />
      )}
      <div
        className="table-content pt-6 flex flex-col overflow-hidden"
        style={{ boxShadow: '0px 0px 25px #2F41A51F' }}
      >
        {/* Header */}
        <SweepsTableHeader
          searchQuery={sweepSearchQuery}
          handleOnChangeSearch={handleUserSearchChange}
          selectedCount={selectedSweeps.length}
          reportLabel={
            selectedSweeps.length > 1
              ? t('common.multi_report')
              : t('sweeps.report_one')
          }
          unitTranslationKey={'sweeps.table.sweeps'}
          selectedDate={selectedDate}
          handleSelectedDate={(date: Date) => setSelectedDate(date)}
          disableSettingMenu={true}
          handleApprove={handleApprove}
          handleClose={handleClearSweeps}
          handleDelete={handleDeleteSweeps}
          handleDownload={handleDownloadSweeps}
          showDownloadLoading={downloadSweepReportLoading}
        >
          <SweepsTableFilter
            handleFilterApply={handleFilterApply}
            handleClearFilter={handleClearFilter}
            sweepsSummary={sweepsSummary}
            isOpenUserModal={openFilterModal}
            toggleFilterModal={(value: boolean) => {
              setOpenFilterModal(value);
            }}
            filters={filters}
            setFilters={setFilters}
            selectedBranchIDs={selectedBranchIDs}
          />
        </SweepsTableHeader>

        {/* Scrollable Table */}
        <div className="flex-grow overflow-y-auto max-h-[700px]">
          <Table
            onSelectionChange={handleSelectionChange}
            tableHeader={getSweepTableHeader(isTablet)}
            selectedRows={selectedSweeps}
            tableData={filteredData ?? []}
            loading={
              showConfirmApproveModal || showDeleteConfirmation
                ? false
                : sweepsSummaryLoading
            }
            onViewDetail={handleViewSweepReportModal}
            onKebabSelect={handleKebabActionFromKebabMenu}
            loadingMenu={loadingActionMenu ?? { rowIndex: -1, action: '' }}
            getKebabMenuOptions={getSweepKebabOptions}
            onShouldCloseKebabMenu={(action) => {
              return [
                SweepKebabOption.ViewEditHistory,
                SweepKebabOption.Edit,
              ].includes(action as SweepKebabOption);
            }}
          />
        </div>

        {showSweepViewModal && (
          <SweepViewModal
            onEdit={() => {
              setShowSweepEditModal(true);
              setShowSweepViewModal(false);
            }}
            onDownload={() => {
              if (selectedSweepRowForModal) {
                handleDownloadSweepReports([selectedSweepRowForModal?._id]);
              }
            }}
            onClose={() => setShowSweepViewModal(false)}
            showDownloadLoading={downloadSweepReportLoading}
          >
            <div>
              <SweepReport
                pdfContentData={{
                  players: selectedSweepRowForModal?.players ?? [],
                  time: selectedSweepRowForModal?.createdOn
                    ? moment(selectedSweepRowForModal?.createdOn).format(
                        'HH:mm',
                      )
                    : '-',
                  hour: selectedSweepRowForModal?.hour,
                  noOfPlayers: selectedSweepRowForModal?.players.filter(
                    (player: Player) => player.presentDuringSweep,
                  ).length,
                  harmObserved: poiCount > 0 && `Yes (${poiCount})`,
                  tag: selectedSweepRowForModal?.tag,
                  sweepType: selectedSweepRowForModal?.type,
                  sweepStatus: selectedSweepRowForModal?.status,
                  completeBySignature:
                    selectedSweepRowForModal?.completedBy?.signature ?? '',

                  completeBySignatureDate: moment(
                    selectedSweepRowForModal?.completedBy?.signedDate,
                  ).format('hh:mm A, DD/MM/YYYY'),
                  reviewedBySignature:
                    selectedSweepRowForModal?.reviewedBy?.signature ?? '',

                  reviewedBySignatureDate: moment(
                    selectedSweepRowForModal?.reviewedBy?.signedDate,
                  ).format('hh:mm A, DD/MM/YYYY'),
                  sweepNo: selectedSweepRowForModal?.sweepNumber,
                  sweepDate: moment(selectedSweepRowForModal?.createdOn).format(
                    'DD/MM/YYYY',
                  ),
                  completedBy: selectedSweepRowForModal?.completedBy,
                  reviewedBy: selectedSweepRowForModal?.reviewedBy,
                }}
                onPlayerHtmlGenerated={(html) => {}}
              />
            </div>
          </SweepViewModal>
        )}
        {showSweepEditModal && (
          <SweepEditModal
            footerButtonText={'userManagement.save'}
            onCloseEditModal={() => {
              setShowSweepEditModal(false);
              setShowSweepViewModal(true);
            }}
            usersSummary={usersSummary}
            selectedSweep={selectedSweepRowForModal}
            setSelectedSweep={setSelectedSweepRowForModal}
          />
        )}
        {showApproveSignatureModal && (
          <SignatureModal
            type={UserRole.MANAGER}
            isOpen={showApproveSignatureModal}
            isBulk={
              selectedApproveRange !== undefined || selectedSweeps.length > 1
            }
            onClose={() => {
              setShowApproveSignatureModal(false);
              setSelectedSweeps([]);
              setSelectedApproveRange(undefined);
            }}
            handleAddEditSignature={handleAddSignature}
            loading={false}
          />
        )}
        {showConfirmApproveModal && (
          <ConfirmationModal
            isOpen={showConfirmApproveModal}
            showConfirmButton
            onClose={() => {
              setShowConfirmApprove(false);
              setSelectedSweeps([]);
              setSelectedApproveRange(undefined);
            }}
            isLoading={approveLoading}
            message={
              selectedApproveRange ? (
                <Trans
                  i18nKey="sweeps.approveDateSelectReports"
                  values={{
                    report:
                      selectedSweeps?.length === 1
                        ? t('sweeps.report_one')
                        : t('sweeps.reports_multiple'),
                    dateRange: `${moment(selectedApproveRange?.from).format(
                      'DD/MM/YYYY',
                    )} - ${moment(selectedApproveRange?.to).format(
                      'DD/MM/YYYY',
                    )}`,
                  }}
                  components={{ bold: <strong /> }}
                  tOptions={{ escapeValue: false }}
                />
              ) : (
                <Trans
                  i18nKey="sweeps.approveSelectedReport"
                  values={{
                    report:
                      selectedSweeps?.length === 1
                        ? t('sweeps.report_one')
                        : t('sweeps.reports_multiple'),
                  }}
                />
              )
            }
            onConfirm={handleApproveConfirm}
          />
        )}
        {showApproveDateModal && (
          <CenterModal
            isOpen={showApproveDateModal}
            showClearButton
            onClose={() => {
              setShowApproveDateModal(false);
              setSelectedApproveRange(undefined);
            }}
            onClear={() => {}}
            title={'sweeps.approveReport'}
            onSubmit={handleNext}
            successText={'sweeps.next'}
            isLoading={false}
            isDisabled={selectedApproveRange ? false : true}
          >
            <div className="text-[#1A1A1A] text-sm mb-7 mt-4">
              <Trans i18nKey={'sweeps.approveText'} />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-[#1A1A1A] text-sm">
                {' '}
                <Trans i18nKey={'sweeps.dateRange'} />
              </div>
              <DatePicker
                finalRange={selectedApproveRange}
                handleApply={handleApply}
                mode={Mode.Range}
              />
            </div>
          </CenterModal>
        )}
        {showAlreadyApproveModal && (
          <CenterModal
            isOpen={showAlreadyApproveModal}
            showClearButton
            showSuccess
            onClose={() => {
              setShowAlreadyApproveModal(false);
            }}
            onClear={() => {}}
            title={''}
            onSubmit={() => setShowAlreadyApproveModal(false)}
            successText={'common.ok'}
            isLoading={false}
            isDisabled={false}
          >
            <div>{SuccessMessage.ALREADY_APPROVE}</div>
          </CenterModal>
        )}
      </div>
      <ModalComponent
        open={showEditHistoryModal}
        modalClassName="h-[830px]"
        dialogClassName="top-[100px] w-[600px]"
        isShowClose
        title={`Edit history —`}
        subTitle={` Sweep #${selectedSweepRowForModal?.sweepNumber}, ${
          selectedSweepRowForModal?.createdOn
            ? moment(selectedSweepRowForModal.createdOn).format('DD/MM/YYYY')
            : ''
        }`}
        onToggle={() => setShowEditHistoryModal(false)}
      >
        {selectedSweepRowForModal && (
          <EditHistoryList selectedSweepSummary={selectedSweepRowForModal} />
        )}
      </ModalComponent>
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        showConfirmButton
        onClose={() => setShowDeleteConfirmation(false)}
        message={
          <Trans
            i18nKey="common.deleteConfirmation"
            values={{
              report: t('common.multi_report'),
            }}
            components={{ bold: <strong /> }}
          />
        }
        isLoading={deleteSweepLoading || sweepsSummaryLoading}
        onConfirm={handleConfirmSweepDelete}
      />
      {showDeleteSuccessToast && (
        <SuccessToast
          message={SuccessMessage.DELETE_REPORT_SUCCESSFULLY}
          onClose={() => {
            setShowDeleteSuccessToast(false);
            setSelectedSweeps([]);
          }}
          className="bottom-15 bg-white right-10"
        />
      )}
      {showDeleteErrorToast && (
        <SuccessToast
          showError
          message={SuccessMessage.DELETE_ERROR}
          onClose={() => setShowDeleteErrorToast(false)}
          className="bottom-15 bg-white right-10"
        />
      )}
    </div>
  );
};

export default Sweeps;
